import { InputPartType } from "@redotech/redo-return-core-common/return-flow-answers";
import { createEnum } from "@redotech/zod-util/zod-enum";
import * as z from "zod";
import { ReturnFlow, Step } from "../../../return-flow";

export enum ReturnReasonType {
  QUESTION_ANSWER = "question_answer",
  AI_RETURN_REASONS = "ai_return_reasons",
}

const BaseReturnReasonSchema = z.object({
  type: createEnum(ReturnReasonType),
  variantExchangeGuidanceForStep: z.string().optional(),
});
const QuestionAnswerReturnReasonSchema = BaseReturnReasonSchema.extend({
  type: z.literal(ReturnReasonType.QUESTION_ANSWER),
  prompt: z.string(),
  answer: z.string(),
  /** Merchant-configured reason code on the selected option, when present. */
  reasonCode: z.string().optional(),
});
const AiReturnReasonReturnReasonsSchema = BaseReturnReasonSchema.extend({
  type: z.literal(ReturnReasonType.AI_RETURN_REASONS),
  aiReturnReasonsThreadId: z.string(),
});

const returnReasonSchema_ = z.discriminatedUnion("type", [
  QuestionAnswerReturnReasonSchema,
  AiReturnReasonReturnReasonsSchema,
]);
export const ReturnReasonSchema: z.ZodType<ReturnReason> = returnReasonSchema_;
export type ReturnReason = z.infer<typeof returnReasonSchema_>;

export function getReturnReasons({
  multipleChoiceAnswers,
  inputAnswers,
  aiReturnReasons,
  returnFlow,
  removeMultipleChoiceNonReturnReasons,
}: {
  multipleChoiceAnswers: readonly { step: number; answer: number }[];
  inputAnswers: readonly {
    message: string;
    value: string | string[] | URL[];
    type: InputPartType;
    step?: number | undefined | null;
  }[];
  aiReturnReasons?: {
    step: number;
    aiReturnReasonsThreadId: string;
    additionalAiInfo?: string;
  };
  returnFlow: ReturnFlow;
  removeMultipleChoiceNonReturnReasons?: boolean;
}): ReturnReason[] {
  // Map multiple choice answers to their questions and response values.
  const multipleChoiceAnswers_ = multipleChoiceAnswers
    .map((choice) => {
      const stepObject = returnFlow.steps[choice.step];
      // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
      if (stepObject.type !== Step.MULTIPLE_CHOICE) {
        return undefined;
      }
      if (removeMultipleChoiceNonReturnReasons && !stepObject.reason) {
        return undefined;
      }
      const choiceOption = stepObject.options[choice.answer];
      if (choiceOption && choiceOption.name) {
        let additionalAiInfo = "";
        if (stepObject.additionalAiInfo) {
          additionalAiInfo += `${stepObject.additionalAiInfo}`;
        }
        if (choiceOption.additionalAiInfo) {
          additionalAiInfo += `${additionalAiInfo ? "\n" : ""}${choiceOption.additionalAiInfo}`;
        }

        return {
          type: ReturnReasonType.QUESTION_ANSWER,
          prompt: stepObject.message,
          answer: choiceOption.name,
          reasonCode: choiceOption.code || undefined,
          variantExchangeGuidanceForStep: additionalAiInfo || undefined,
          step: choice.step,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  // Filter inputs down to text answers
  const inputAnswers_ = inputAnswers
    .map((answer) => {
      if (
        answer.type === InputPartType.TEXTAREA &&
        typeof answer.value === "string"
      ) {
        return {
          type: ReturnReasonType.QUESTION_ANSWER,
          prompt: answer.message,
          answer: answer.value,
          // Additional ai info is currently only supported for multiple choice answers.
          variantExchangeGuidanceForStep: undefined,
          step: answer.step ?? 0,
        };
      }
      return undefined;
    })
    .filter((answer) => answer !== undefined);

  const aiReturnReasons_ = aiReturnReasons
    ? [
        {
          type: ReturnReasonType.AI_RETURN_REASONS,
          aiReturnReasonsThreadId: aiReturnReasons.aiReturnReasonsThreadId,
          variantExchangeGuidanceForStep:
            aiReturnReasons.additionalAiInfo || undefined,
          step: aiReturnReasons.step,
        },
      ]
    : [];

  return [...multipleChoiceAnswers_, ...inputAnswers_, ...aiReturnReasons_]
    .sort((a, b) => a.step - b.step)
    .map((response) => returnReasonSchema_.parse(response));
}
